import each from 'lodash/each';
import isObject from 'lodash/isObject';
import VueScript2 from 'vue-script2';

import logger from '../../utils/logger';
import config from 'config';

class GoogleAnalytics {
  async _init(rootState, state, commit, id, isNotCampaign, isWlClientId) {
    let response;

    if (!state.currentIds.googleAnalytics.includes(id)) {
      response = await VueScript2.load(`https://www.googletagmanager.com/gtag/js?id=${id}`);

      let custom_map;

      if (isNotCampaign) {
        if (rootState.isWhitelabel && !isWlClientId) {
          custom_map = {
            dimension1: 'vsClientId',
            dimension2: 'vsCampaignId',
            dimension3: 'url',
            dimension4: 'size',
          };
        } else {
          custom_map = {
            metric1: 'sale',
            dimension2: 'merchant',
            dimension4: 'vsClientId',
            dimension5: 'vsCampaignId',
            dimension7: 'size',
          };
        }
      }

      gtag('config', id, {
        send_page_view: false,
        custom_map,
      });

      commit('trackId', { property: 'googleAnalytics', value: id });

      logger.log(`${id} initialize.`);
    } else {
      logger.log(`${id} already initialized.`);

      response = Promise.resolve();
    }

    return response;
  }

  _id(wlId) {
    return wlId || config.GA_ID;
  }

  _payloadFromCart(state) {
    let products = [];

    each(state.cart.items, item => {
      let product = {
        id: `${item.product_id}`,
        name: `${item.product_sku}`,
        variant: `${item.color}`,
        price: Math.round(item.price_per_unit * 100) / 100,
        quantity: item.quantity,
        size: `${item.size}`,
      };

      products.push(product);
    });

    return products;
  }

  _vsInit(rootState, state, commit, wlId) {
    logger.groupCollapsed(`${wlId ? 'WL' : 'VS'} Google Analytics`);

    return this._init(rootState, state, commit, this._id(wlId), true, wlId);
  }

  _vsCleanUp(message, wlId, payload, end = true) {
    if (isObject(payload)) {
      if (!Object.keys(payload).length) {
        payload = null;
      }
    }

    if (payload) {
      logger.log(`${this._id(wlId)} ${message} event with payload:`);

      logger.log(payload);
    } else {
      logger.log(`${this._id(wlId)} ${message} event`);
    }

    if (end) {
      logger.groupEnd();
    }
  }

  viewPage({ rootState, state, commit }, wlId) {
    return new Promise(resolve => {
      setTimeout(async () => { // hack to let vue router resolve
        let dimensions = {};

        await this._vsInit(rootState, state, commit, wlId);

        if (rootState.isWhitelabel) {
          // TODO: we might have to send these every time because of the GA4 works
          if (!wlId) {
            dimensions.vsClientId = `${rootState.domainConfig.client_id}`;
            dimensions.url = `${rootState.domainConfig.domain}`;
          }

          if (['checkout', 'cart'].includes(rootState.route.name)) {
            dimensions.page_path = `/${rootState.route.name}`;
          }
        }

        gtag('event', 'page_view', {
          send_to: this._id(wlId),
          ...dimensions,
        });

        this._vsCleanUp('page view', wlId, dimensions);

        resolve();
      }, 1000);
    });
  }

  async vsViewSales({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    let dimensions = {};

    if (rootState.isWhitelabel) {
      if (wlId) {
        dimensions = {
          page_path: `${state.campaign.url}`,
        };
      } else {
        dimensions = {
          vsClientId: `${rootState.domainConfig.client_id}`,
          vsCampaignId: `${rootState.sales.campaign.analytics_cid}`,
          url: `${rootState.domainConfig.domain}`,
          page_path: '/salespage',
        };
      }
    } else {
      dimensions = {
        vsClientId: `${state.campaign.analytics_uid}`,
        vsCampaignId: `${state.campaign.analytics_cid}`,
        page_path: '/salespage',
      };
    }

    gtag('event', 'view_item', {
      send_to: this._id(wlId),
      items: [state.product],
    });

    gtag('event', 'page_view', {
      send_to: this._id(wlId),
      ...dimensions,
    });

    this._vsCleanUp('view item', wlId, { items: [state.product] }, false);

    this._vsCleanUp('page view', wlId, dimensions);

    return response;
  }

  async vsViewThankYou({ rootState, state, commit }, wlId) {
    let value = 0,
      payload = {
        transaction_id: `${state.order.order_lookup_number}`,
        shipping: Math.round((state.order.shipping_total - state.order.shipping_discount_total) * 100) / 100,
        tax: Math.round(state.order.tax_total * 100) / 100,
        currency: 'USD',
        items: [],
      },
      dimensions = {};

    if (!rootState.isWhitelabel) {
      dimensions = {
        sale: '1',
        merchant: state.order.payment.method,
        page_path: '/thankyou',
      };
    }

    let response = await this._vsInit(rootState, state, commit, wlId);

    each(state.order.orders, order => {
      each(order.products, item => {
        let product = {
          id: `${item.product_id}`,
          name: `${item.product_sku}`,
          variant: `${item.color}`,
          price: Math.round((item.discounted_price / item.quantity) * 100) / 100,
          quantity: item.quantity,
          size: `${item.size}`,
        };

        value += item.discounted_price;

        payload.items.push(product);
      });
    });

    payload.value = Math.round(value * 100) / 100;

    gtag('event', 'purchase', {
      send_to: this._id(wlId),
      ...payload,
    });

    gtag('event', 'page_view', {
      send_to: this._id(wlId),
      ...dimensions,
    });

    this._vsCleanUp('purchase', wlId, payload, false);

    this._vsCleanUp('page view', wlId, dimensions);

    return response;
  }

  async vsAddToCart({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    let items = [{
      size: state.size,
      quantity: state.quantity,
      ...state.product,
    }];

    gtag('event', 'add_to_cart', {
      send_to: this._id(wlId),
      items,
    });

    this._vsCleanUp('add to cart', wlId, items);

    return response;
  }

  async vsRemoveFromCart({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    let items = [{
      size: state.size,
      quantity: state.quantity,
      ...state.product,
    }];

    gtag('event', 'remove_from_cart', {
      send_to: this._id(wlId),
      items,
    });

    this._vsCleanUp('remove from cart', wlId, items);

    return response;
  }

  async vsChangeQuantity({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    gtag('event', 'select', {
      send_to: this._id(wlId),
      event_category: 'UX',
      event_label: 'Product Quantity',
      event_value: state.quantity,
    });

    this._vsCleanUp('change quantity', wlId, state.quantity);

    return response;
  }

  async vsChangeColor({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    gtag('event', 'select', {
      send_to: this._id(wlId),
      event_category: 'UX',
      event_label: 'Product Color',
      event_value: state.product.variant,
    });

    this._vsCleanUp('change color', wlId, state.product.variant);

    return response;
  }

  async vsChangeSize({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    gtag('event', 'select', {
      send_to: this._id(wlId),
      event_category: 'UX',
      event_label: 'Product Size',
      event_value: state.size,
    });

    this._vsCleanUp('change size', wlId, state.size);

    return response;
  }

  async vsApplyCoupon({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    gtag('event', 'click', {
      send_to: this._id(wlId),
      event_category: 'UX',
      event_label: 'Apply Coupon',
      event_value: state.coupon,
    });

    this._vsCleanUp('apply coupon', wlId, state.coupon);

    return response;
  }

  async vsInitiateCheckout({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    let items = this._payloadFromCart(state);

    gtag('event', 'begin_checkout', {
      send_to: this._id(wlId),
      currency: 'USD',
      items,
    });

    this._vsCleanUp('begin checkout', wlId, items);

    return response;
  }

  async vsInitiateCheckoutUpsell({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    gtag('event', 'begin_checkout', {
      send_to: this._id(wlId),
      currency: 'USD',
    });

    this._vsCleanUp('initaite checkout upsell', wlId);

    return response;
  }

  async vsCompleteOrder({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    let items = this._payloadFromCart(state),
      checkoutParams = {};

    if (rootState.isWhitelabel && wlId) {
      checkoutParams = {
        checkout_step: 2,
      };
    } else {
      checkoutParams = {
        checkout_option: state.merchant,
        checkout_step: 2,
      };
    }

    gtag('event', 'checkout_progress', {
      send_to: this._id(wlId),
      currency: 'USD',
      items,
      ...checkoutParams,
    });

    this._vsCleanUp('complete order', wlId, items);

    return response;
  }

  async vsCompleteOrderUpsell({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    let checkoutParams = {};

    if (rootState.isWhitelabel && wlId) {
      checkoutParams = {
        checkout_step: 2,
      };
    } else {
      checkoutParams = {
        checkout_option: state.merchant,
        checkout_step: 2,
      };
    }

    gtag('event', 'checkout_progress', {
      send_to: this._id(wlId),
      currency: 'USD',
      ...checkoutParams,
    });

    this._vsCleanUp('complete order upsell', wlId);

    return response;
  }

  async vsContinueShopping({ rootState, state, commit }, wlId) {
    let response = await this._vsInit(rootState, state, commit, wlId);

    gtag('event', 'click', {
      send_to: this._id(wlId),
      event_category: 'UX',
      event_label: 'Continue Shopping',
    });

    this._vsCleanUp('continue shopping', wlId);

    return response;
  }

  clientViewSales({ rootState, state, commit }) {
    logger.groupCollapsed('Client Google Analytics');

    let id = state.campaign.tracking.google_analytics_id;

    if (id) {
      this._init(rootState, state, commit, id);

      gtag('event', 'page_view', {
        send_to: id,
        page_path: `/${state.campaign.url}`,
      });

      logger.log(`${id} page view event`);
    } else {
      logger.log('Not configured');
    }

    logger.groupEnd();
  }

  clientViewCollection({ rootState, state, commit }) {
    logger.groupCollapsed('Client Google Analytics');

    let id = rootState.collection.info.tracking.google_analytics_id;

    if (id) {
      this._init(rootState, state, commit, id);

      gtag('event', 'page_view', { send_to: id });

      logger.log(`${id} page view event`);
    } else {
      logger.log('Not configured');
    }

    logger.groupEnd();
  }

  clientViewThankYou({ rootState, state, commit }) {
    logger.groupCollapsed('Client Google Analytics');

    each(state.order.orders, order => {
      logger.groupCollapsed(order.campaign_name);

      let id = state.order.tracking[order.analytics_cid].google_analytics_id;

      if (id) {
        this._init(rootState, state, commit, id);

        let items = [];

        each(order.products, (product, index) => {
          let item = {
            id: `${state.order.order_lookup_number}-${index}`,
            price: product.price,
            sku: product.product_sku,
            name: order.campaign_name,
            category: order.campaign_category,
            quantity: product.quantity,
          }

          items.push(item);
        });

        let payload = {
          id: state.order.order_lookup_number,
          affiliation: order.campaign_slug,
          value: Math.round((order.sale_total + order.shipping_discount - order.shipping_total - order.tax_total) * 100) / 100,
          shipping: Math.round((order.shipping_total - order.shipping_discount) * 100) / 100,
          tax: Math.round(order.tax_total * 100) / 100,
          items,
        };

        gtag('event', 'purchase', {
          send_to: id,
          ...payload,
        });

        gtag('event', 'page_view', { send_to: id });

        logger.log(`${id} purchase event with payload:`);

        logger.log(payload);

        logger.log(`${id} page view event`);
      } else {
        logger.log('Not configured');
      }

      logger.groupEnd();
    });

    logger.groupEnd();
  }

  clientCompleteOrderUpsell({ rootState, state, commit }) {
    logger.groupCollapsed('Client Google Analytics');

    let id = state.upsell.tracking.google_analytics_id;

    if (id) {
      this._init(rootState, state, commit, id);

      let order = state.order.orders[state.order.orders.length - 1],
        product = order.products[0],
        pricePerItem = Math.round((product.price / product.quantity) * 100) / 100;

      let item = {
        id: state.order.order_lookup_number,
        price: pricePerItem,
        sku: product.product_sku,
        name: product.campaign_name,
        category: order.campaign_category,
        quantity: product.quantity,
      }

      let payload = {
        id: state.order.order_lookup_number,
        affiliation: order.campaign_slug,
        price: Math.round((order.sale_total + order.shipping_discount - order.shipping_total - order.tax_total) * 100) / 100,
        shipping: Math.round((order.shipping_total - order.shipping_discount) * 100) / 100,
        tax: Math.round(order.tax_total * 100) / 100,
        items: [item],
      };

      gtag('event', 'purchase', {
        send_to: id,
        ...payload,
      });

      gtag('event', 'page_view', { send_to: id });

      logger.log(`${id} purchase event with payload:`);

      logger.log(payload);

      logger.log(`${id} page view event`);
    } else {
      logger.log('Not configured');
    }

    logger.groupEnd();
  }
};

export default new GoogleAnalytics();